/**
 * Company edit PATCH payload builder (SF-3475 whitelist).
 * Lives in its own module so the page file only exports a component
 * (react-refresh) and the rule is unit-testable without rendering Refine.
 *
 * SF-4373: the chargeable "Use custom sending domain" flag is no longer part of
 * the Edit form — it is saved on its own from the CSAT Custom Domain tab
 * (CsatDomainTab), so an Edit save can never touch it.
 *
 * SF-4576: same for `mcp_access` (external AI access): saved on its own by
 * McpAccessSwitch on the detail page. Turning it off cuts live connections,
 * so an Edit save must never carry it.
 *
 * SF-4656: `is_active`, `account_type` and `sales_channel` moved to the
 * detail page too (each saved on its own), and `sso_enabled` lives on the
 * Feature Flags tab. The Edit form now only carries the fields below.
 */
import type { Color } from 'antd/es/color-picker';

/**
 * Whitelist of fields the Edit form may send to the sf-api admin PATCH
 * endpoint. The server accepts more (ADMIN_PATCHABLE_FIELDS in sf-api
 * companyService.js), but every other field is saved by its own control on
 * the detail page.
 */
export const PATCH_FIELDS = ['name', 'color', 'privacy_link'] as const;

export interface CompanyEditFormValues {
  name: string;
  color?: Color | string;
  privacy_link?: string;
}

/**
 * Build the surgical PATCH payload from PATCH_FIELDS only — mirrors the
 * server whitelist so the request never carries stray keys. Exported for tests.
 */
export function buildCompanyPatchPayload(v: CompanyEditFormValues): Record<string, unknown> {
  // Coerce AntD ColorPicker output to a hex string; leave undefined alone so
  // we don't overwrite an existing colour with null. ColorPicker can return
  // null when cleared; treat null and undefined the same so we never send
  // `color: null` and clobber an existing brand colour.
  const colorHex =
    v.color && typeof v.color === 'object' && 'toHexString' in v.color
      ? (v.color as Color).toHexString()
      : ((v.color as string | undefined | null) ?? undefined);

  const payload: Record<string, unknown> = {};
  const source = v as unknown as Record<string, unknown>;
  PATCH_FIELDS.forEach((key) => {
    if (key === 'color') {
      if (colorHex != null) payload.color = colorHex;
      return;
    }
    const value = source[key];
    if (value !== undefined) payload[key] = value;
  });
  return payload;
}
